import React from 'react';

const SkillAnalysisSimple = React.memo(({ skills = [] }) => {
  // Process skills data safely - extract only skill names
  const processedSkills = React.useMemo(() => {
    if (!skills || !Array.isArray(skills)) {
      console.log('Skills data is not an array:', skills);
      return [];
    }
    
    console.log('Raw skills received:', skills);
    
    const processed = skills.map((skill, index) => {
      if (!skill) return null;
      
      // Handle different possible data structures
      let skillName = '';
      let demandValue = 0;
      let ratingValue = 0;
      
      if (typeof skill === 'string') {
        // If skill is just a string
        skillName = skill;
        demandValue = Math.floor(Math.random() * 50) + 25; // Random 25-75%
        ratingValue = 3.5; // Default rating
      } else if (typeof skill === 'object') {
        // If skill is an object, extract the name
        let rawName = skill.name || skill.skill || skill.title || `Skill ${index + 1}`;
        
        // Check if the name is a stringified JSON object
        if (typeof rawName === 'string' && rawName.startsWith('{')) {
          try {
            const parsedName = JSON.parse(rawName);
            skillName = parsedName.name || parsedName.skill || parsedName.title || `Skill ${index + 1}`;
            // Try to get rating from the parsed JSON too
            ratingValue = Number(parsedName.rating || parsedName.experience || skill.rating || skill.experience || 3.5);
          } catch {
            // If parsing fails, use the raw string
            skillName = rawName;
            ratingValue = Number(skill.rating || skill.experience || 3.5);
          }
        } else {
          skillName = rawName;
          ratingValue = Number(skill.rating || skill.experience || 3.5);
        }
        
        demandValue = Number(skill.demand || skill.demandPercentage || skill.demand_percentage || 0);
      }
      
      if (!skillName) return null;
      
      return {
        id: `skill-${index}`,
        name: skillName,
        demand: demandValue,
        rating: ratingValue
      };
    }).filter(Boolean);
    
    console.log('Processed skills:', processed);
    return processed;
  }, [skills]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills in Demand</h3>
      
      {processedSkills.length === 0 ? (
        <div className="text-gray-500 text-center py-4">
          No skills data available
        </div>
      ) : (
        <div className="space-y-4">
          {processedSkills.map((skill) => (
            <div key={skill.id} className="border-l-4 border-blue-500 pl-4 py-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-800">
                  {skill.name}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  ⭐ {skill.rating.toFixed(1)}/5
                </span>
              </div>
              
              {/* Market Demand Progress Bar */}
              <div className="mb-1">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Market Demand</span>
                  <span className="font-medium">{skill.demand.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out" 
                    style={{ width: `${Math.min(Math.max(skill.demand, 0), 100)}%` }}
                  />
                </div>
              </div>
              
              {/* User Rating Progress Bar */}
              <div className="mb-1">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Your Proficiency</span>
                  <span className="font-medium">{((skill.rating / 5) * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500 ease-out" 
                    style={{ width: `${Math.min(Math.max((skill.rating / 5) * 100, 0), 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default SkillAnalysisSimple;